import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Sort, type SortCallbackData, type SortOption } from '@shared/components/ui/Sort';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { useClientRuleConditionValueSelect } from '@modules/configuration/Rules/hooks/useClientRuleConditionValueSelect';
import { faXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BoxGroupsSelector } from '../display/BoxGroupsSelector';
import {
  BoxGroupModel,
  CreateBoxExceptionConditionPayload,
  CreateBoxExceptionPayload,
  PackagingRuleField,
  PackagingRuleOperator,
} from '../../models/PackagingRulesets';
import { ExceptionConditionValueField } from './ExceptionConditionValueField';
import {
  ConditionFormRow,
  buildFinalConditions,
  createConditionRow,
  getDefaultOperatorForField,
  isBooleanField,
  isConditionRowValid,
  isIdField,
  NUMERIC_COMPARISON_FIELDS,
} from './exceptionConditionConfig';

export interface CreateExceptionSubmitPayload {
  exception: CreateBoxExceptionPayload;
  conditions: CreateBoxExceptionConditionPayload[];
}

export interface CreateExceptionModalRef {
  open: () => void;
  close: () => void;
}

interface CreateExceptionModalProps {
  teamId?: number;
  boxGroups: BoxGroupModel[];
  countries: { id: number; name: string; iso2: string }[];
  boxGroupsSearchValue: string;
  onBoxGroupsSearchChange: (value: string) => void;
  boxGroupsSortValue: 'id_desc' | 'id_asc' | 'name_asc' | 'name_desc';
  onBoxGroupsSortChange: (value: 'id_desc' | 'id_asc' | 'name_asc' | 'name_desc') => void;
  hasBoxGroupsSortSelection: boolean;
  hasMoreBoxGroups?: boolean;
  isFetchingMoreBoxGroups?: boolean;
  isBoxGroupsLoading?: boolean;
  onLoadMoreBoxGroups?: () => void;
  isSubmitting?: boolean;
  onSubmit: (payload: CreateExceptionSubmitPayload) => Promise<void> | void;
}

export const CreateExceptionModal = forwardRef<CreateExceptionModalRef, CreateExceptionModalProps>(
  (
    {
      teamId,
      boxGroups,
      countries,
      boxGroupsSearchValue,
      onBoxGroupsSearchChange,
      boxGroupsSortValue,
      onBoxGroupsSortChange,
      hasBoxGroupsSortSelection,
      hasMoreBoxGroups = false,
      isFetchingMoreBoxGroups = false,
      isBoxGroupsLoading = false,
      onLoadMoreBoxGroups,
      isSubmitting,
      onSubmit,
    },
    ref,
  ) => {
    const { t } = useTranslation('packagingRulesetsModule');
    const modalRef = useRef<ModalCenterRef>(null);
    const productSelect = useClientRuleConditionValueSelect(teamId, 'product_id');
    const packSelect = useClientRuleConditionValueSelect(teamId, 'pack_id');

    const [name, setName] = useState('');
    const [conditionRows, setConditionRows] = useState<ConditionFormRow[]>([createConditionRow()]);
    const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const resetForm = () => {
      setName('');
      setConditionRows([createConditionRow()]);
      setSelectedGroupId(null);
      setSubmitError(null);
    };

    useImperativeHandle(ref, () => ({
      open: () => {
        resetForm();
        modalRef.current?.openModal();
      },
      close: () => modalRef.current?.closeModal(),
    }));

    const updateConditionRow = (id: string, updater: (row: ConditionFormRow) => ConditionFormRow) => {
      setConditionRows((prev) => prev.map((row) => (row.id === id ? updater(row) : row)));
    };

    const handleFieldChange = (id: string, newField: string) => {
      updateConditionRow(id, (row) => ({
        ...row,
        field: newField as PackagingRuleField,
        value: '',
        boolValue: false,
        operator: getDefaultOperatorForField(newField as PackagingRuleField),
      }));
    };

    const fieldOptions = [
      { value: 'campaign', label: t('specialRulesModal.fields.special_campaign') },
      { value: 'total_amount', label: t('specialRulesModal.fields.total_amount') },
      { value: 'fragile', label: t('specialRulesModal.fields.fragile') },
      { value: 'country', label: t('specialRulesModal.fields.country') },
      { value: 'product_id', label: t('specialRulesModal.fields.product_id') },
      { value: 'pack_id', label: t('specialRulesModal.fields.pack_id') },
    ];

    const idOperatorOptions = [
      { value: '=', label: t('specialRulesModal.operators.includes') },
      { value: '!=', label: t('specialRulesModal.operators.notIncludes') },
    ];

    const numericOperatorOptions = [
      { value: '>', label: t('specialRulesModal.operators.gt') },
      { value: '<', label: t('specialRulesModal.operators.lt') },
      { value: '>=', label: t('specialRulesModal.operators.gte') },
      { value: '<=', label: t('specialRulesModal.operators.lte') },
      { value: '=', label: t('specialRulesModal.operators.eq') },
      { value: '!=', label: t('specialRulesModal.operators.neq') },
    ];

    const stringOperatorOptions = [
      { value: '=', label: t('specialRulesModal.operators.eq') },
      { value: '!=', label: t('specialRulesModal.operators.neq') },
      { value: 'contains', label: t('specialRulesModal.operators.contains') },
    ];

    const getOperatorOptions = (row: ConditionFormRow) => {
      if (isIdField(row)) return idOperatorOptions;
      if (isBooleanField(row)) return stringOperatorOptions.filter((o) => ['=', '!='].includes(o.value));
      if (NUMERIC_COMPARISON_FIELDS.includes(row.field)) return numericOperatorOptions;
      return stringOperatorOptions;
    };

    const boxGroupsSortOptions = useMemo(
      (): SortOption[] => [
        { field: 'id', direction: 'desc', label: 'Mas nuevo a mas viejo' },
        { field: 'id', direction: 'asc', label: 'Mas viejo a mas nuevo' },
        { field: 'name', direction: 'asc', label: 'Alfabetico A-Z' },
        { field: 'name', direction: 'desc', label: 'Alfabetico Z-A' },
      ],
      [],
    );

    const parseSortValue = (sort: 'id_desc' | 'id_asc' | 'name_asc' | 'name_desc') => {
      if (sort === 'id_asc') return { field: 'id', direction: 'asc' as const };
      if (sort === 'name_asc') return { field: 'name', direction: 'asc' as const };
      if (sort === 'name_desc') return { field: 'name', direction: 'desc' as const };
      return { field: 'id', direction: 'desc' as const };
    };

    const toSortValue = (f: string, direction: 'asc' | 'desc'): 'id_desc' | 'id_asc' | 'name_asc' | 'name_desc' => {
      if (f === 'id' && direction === 'asc') return 'id_asc';
      if (f === 'name' && direction === 'asc') return 'name_asc';
      if (f === 'name' && direction === 'desc') return 'name_desc';
      return 'id_desc';
    };

    const addConditionRow = () => {
      setConditionRows((prev) => [...prev, createConditionRow()]);
    };

    const removeCondition = (id: string) => {
      setConditionRows((prev) => (prev.length > 1 ? prev.filter((row) => row.id !== id) : prev));
    };

    const canSubmit =
      name.trim().length > 0 &&
      selectedGroupId !== null &&
      conditionRows.length > 0 &&
      conditionRows.every(isConditionRowValid);

    const handleCreate = async () => {
      if (!canSubmit || !selectedGroupId) return;
      setSubmitError(null);

      await onSubmit({
        exception: {
          name: name.trim(),
          box_group_id: selectedGroupId,
          priority: 100,
          active: true,
        },
        conditions: buildFinalConditions(conditionRows),
      });

      modalRef.current?.closeModal();
    };

    return (
      <ModalCenter
        ref={modalRef}
        title={t('specialRulesModal.title')}
        type="default"
        width="lg"
        data-testid="create-box-exception-modal"
      >
        <div className="flex h-[78vh] max-h-[78vh] flex-col">
          <div className="space-y-4 overflow-y-auto pr-1 flex-1" data-testid="create-box-exception-modal-content">
            <div className="space-y-1.5">
              <label className="text-sm text-text">{t('specialRulesModal.name')}</label>
              <Input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder={t('specialRulesModal.namePlaceholder')}
                data-testid="create-exception-name-input"
              />
            </div>

            <div className="space-y-3">
              <div>
                <p className="text-sm text-text font-medium">{t('specialRulesModal.condition')}</p>
                <p className="text-xs text-subtext mt-0.5">{t('specialRulesModal.conditionLogicHint')}</p>
              </div>

              {conditionRows.map((row, idx) => (
                <div key={row.id} className="space-y-2 rounded-md border border-card-border p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-subtext">Condición {idx + 1}</p>
                    {conditionRows.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-subtext hover:text-error shrink-0"
                        onClick={() => removeCondition(row.id)}
                        aria-label={t('specialRulesModal.removeConditionAria')}
                        data-testid={`create-exception-remove-condition-${idx}`}
                      >
                        <FontAwesomeIcon icon={faXmark} className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>

                  {idx > 0 && (
                    <Combobox
                      options={[
                        { value: 'AND', label: t('specialRulesModal.separatorAnd') },
                        { value: 'OR', label: t('specialRulesModal.separatorOr') },
                      ]}
                      value={row.joinWithPrevious}
                      setValue={(v) =>
                        updateConditionRow(row.id, (current) => ({
                          ...current,
                          joinWithPrevious: v as 'AND' | 'OR',
                        }))
                      }
                      placeholder={t('specialRulesModal.separatorAnd')}
                      dataTestId={`create-exception-join-${idx}`}
                    />
                  )}

                  <Combobox
                    options={fieldOptions}
                    value={row.field}
                    setValue={(newField) => handleFieldChange(row.id, newField)}
                    placeholder={t('specialRulesModal.fieldPlaceholder')}
                    dataTestId={`create-exception-field-combobox-${idx}`}
                  />

                  {isIdField(row) && (
                    <p className="text-xs text-subtext">{t(`specialRulesModal.fieldHints.${row.field}`)}</p>
                  )}

                  {isBooleanField(row) ? (
                    <div className="grid grid-cols-2 gap-2">
                      <Combobox
                        options={getOperatorOptions(row)}
                        value={row.operator}
                        setValue={(v) =>
                          updateConditionRow(row.id, (current) => ({
                            ...current,
                            operator: v as PackagingRuleOperator,
                          }))
                        }
                        placeholder={t('specialRulesModal.operatorPlaceholder')}
                        dataTestId={`create-exception-operator-combobox-${idx}`}
                      />
                      <Combobox
                        options={[
                          { value: 'true', label: t('specialRulesModal.boolean.true') },
                          { value: 'false', label: t('specialRulesModal.boolean.false') },
                        ]}
                        value={row.boolValue ? 'true' : 'false'}
                        setValue={(v) =>
                          updateConditionRow(row.id, (current) => ({
                            ...current,
                            boolValue: v === 'true',
                          }))
                        }
                        placeholder={t('specialRulesModal.valuePlaceholder')}
                        dataTestId={`create-exception-value-boolean-${idx}`}
                      />
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      <Combobox
                        options={getOperatorOptions(row)}
                        value={row.operator}
                        setValue={(v) =>
                          updateConditionRow(row.id, (current) => ({
                            ...current,
                            operator: v as PackagingRuleOperator,
                          }))
                        }
                        placeholder={t('specialRulesModal.operatorPlaceholder')}
                        dataTestId={`create-exception-operator-combobox-${idx}`}
                      />
                      <ExceptionConditionValueField
                        row={row}
                        idx={idx}
                        countries={countries}
                        productSelect={productSelect}
                        packSelect={packSelect}
                        onValueChange={(value) =>
                          updateConditionRow(row.id, (current) => ({
                            ...current,
                            value,
                          }))
                        }
                        t={t}
                        testIdPrefix="create-exception"
                      />
                    </div>
                  )}
                </div>
              ))}

              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addConditionRow}
                  data-testid="create-exception-add-condition-button"
                >
                  {t('specialRulesModal.addCondition')}
                </Button>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm text-text">{t('specialRulesModal.boxGroup')}</label>
              <BoxGroupsSelector
                groups={boxGroups}
                selectedGroupId={selectedGroupId}
                onSelectGroup={(group) => {
                  setSelectedGroupId(group.id);
                  setSubmitError(null);
                }}
                isLoading={isBoxGroupsLoading}
                emptyText={t('specialRulesModal.noBoxGroups')}
                searchValue={boxGroupsSearchValue}
                onSearchChange={onBoxGroupsSearchChange}
                searchPlaceholder={t('boxes.defaultGroup.searchPlaceholder')}
                searchTestId="create-exception-box-groups-search-input"
                sortSlot={
                  <div className="shrink-0">
                    <Sort
                      options={boxGroupsSortOptions}
                      onSort={(data: SortCallbackData) =>
                        onBoxGroupsSortChange(toSortValue(data.field, data.direction))
                      }
                      initialValue={hasBoxGroupsSortSelection ? parseSortValue(boxGroupsSortValue) : undefined}
                      defaultLabel="Ordenar"
                      dataTestId="create-exception-box-groups-sort"
                      size="default"
                      className="h-10 w-full justify-between"
                    />
                  </div>
                }
                testIdPrefix="create-exception-group"
                emptyTestId="create-exception-group-empty"
                hasMore={hasMoreBoxGroups}
                isFetchingMore={isFetchingMoreBoxGroups}
                onLoadMore={onLoadMoreBoxGroups}
                loadMoreTestId="create-exception-group-load-more"
              />
            </div>

            {submitError && (
              <p className="text-sm text-error" data-testid="create-exception-submit-error">
                {submitError}
              </p>
            )}
          </div>

          <div className="mt-3 flex justify-end gap-2 border-t border-card-border pt-4">
            <Button
              variant="outline"
              size="lg"
              onClick={() => modalRef.current?.closeModal()}
              data-testid="create-exception-cancel-button"
            >
              {t('specialRulesModal.cancel')}
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={handleCreate}
              disabled={!canSubmit || Boolean(isSubmitting)}
              data-testid="create-exception-submit-button"
            >
              {isSubmitting ? t('specialRulesModal.submitting') : t('specialRulesModal.submit')}
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

CreateExceptionModal.displayName = 'CreateExceptionModal';
